/**
 * The customer's canvas hanging in one of the shop's rooms.
 *
 * The room is a photograph the shop has set up (Products > Rooms): measured,
 * so the canvas hangs at its real size, and with the sofa, wall and so on
 * outlined so the customer can repaint them to look like their own room. The
 * question a customer is really asking - "will this work above my sofa?" - is
 * answered better by their sofa's colour than by a 3D model.
 *
 * Flat, like the customer's own-wall photo and for the same reason: the
 * photograph already has its perspective and light, and a flat canvas against
 * a straight-on wall is what looks real. The last tab is that own-wall photo,
 * for a customer who would rather see their actual wall.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { RoomPainter, loadPhoto } from '../rooms/painter';
import { canvasShare, hex, type Point } from '../rooms/types';
import type { PublicRoom, ThreeConfig } from '../three/types';
import { CanvasPanels, useCanvasArt, wholeWidth } from './CanvasArt';
import { WallPhoto } from './WallPhoto';

/** Which tab: a room by its index, or the customer's own wall. */
type Tab = number | 'own';

export function RoomView({ config, rooms }: { config: ThreeConfig; rooms: PublicRoom[] }) {
  const canvas = config.canvas!;
  const art = useCanvasArt();
  const [tab, setTab] = useState<Tab>(rooms.length ? 0 : 'own');
  const [ownPhoto, setOwnPhoto] = useState<HTMLImageElement | null>(null);

  const room = typeof tab === 'number' ? rooms[tab] ?? null : null;

  return (
    <div className="tb-room">
      <div className="tb-room__tabs" role="tablist" aria-label="Where it is shown">
        {rooms.map((each, index) => (
          <button
            key={each.id}
            type="button"
            role="tab"
            aria-selected={tab === index}
            className={`tb-room__tab${tab === index ? ' is-on' : ''}`}
            onClick={() => setTab(index)}
          >
            {each.name}
          </button>
        ))}
        <button type="button" role="tab" aria-selected={tab === 'own'} className={`tb-room__tab${tab === 'own' ? ' is-on' : ''}`} onClick={() => setTab('own')}>
          Your own wall
        </button>
      </div>

      {room ? (
        // Keyed by room, so each keeps nothing of the last one's drawing.
        <Room key={room.id} room={room} canvas={canvas} art={art} />
      ) : (
        <OwnWall config={config} photo={ownPhoto} onPhoto={setOwnPhoto} />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * One room
 * ----------------------------------------------------------------------- */

/**
 * Colours chosen and where the canvas was dragged to, per room, for as long
 * as the designer is open - so going to another room and back finds this one
 * as it was left.
 */
const remembered = new Map<number, { colours: Record<string, string | null>; hang: Point }>();

function Room({ room, canvas, art }: { room: PublicRoom; canvas: NonNullable<ThreeConfig['canvas']>; art: string[] }) {
  const kept = remembered.get(room.id);
  const [colours, setColours] = useState<Record<string, string | null>>(kept?.colours ?? {});
  const [hang, setHang] = useState<Point>(kept?.hang ?? room.hang);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    remembered.set(room.id, { colours, hang });
  }, [room.id, colours, hang]);

  /* --- fitting the photograph into the space there is ----------------- */

  const stage = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const element = stage.current;

    if (!element) return;

    const ratio = room.photo.h / room.photo.w;

    // Contained: as big as fits, never cropped, never stretched.
    const fit = () => {
      const { width, height } = element.getBoundingClientRect();
      const w = Math.min(width, height / ratio);

      setBox({ w: Math.floor(w), h: Math.floor(w * ratio) });
    };

    const observer = new ResizeObserver(fit);

    observer.observe(element);
    fit();

    return () => observer.disconnect();
  }, [room.photo.w, room.photo.h]);

  /* --- repainting ---------------------------------------------------------- */

  const painter = useRef<RoomPainter | null>(null);
  const view = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;

    loadPhoto(room.photo.url)
      .then((image) => {
        if (!alive) return;

        painter.current = new RoomPainter(image);
        setReady(true);
      })
      .catch(() => {
        // The photograph still shows; it just cannot be repainted.
        if (alive) setProblem('Colours cannot be changed on this photograph.');
      });

    return () => {
      alive = false;
    };
  }, [room.photo.url]);

  useEffect(() => {
    if (!ready || !painter.current || !view.current) return;

    // A frame's delay: dragging a colour picker fires far faster than a repaint.
    const frame = requestAnimationFrame(() => {
      const painted = painter.current!.paint(room.objects, colours);
      const out = view.current;

      if (!out) return;

      out.width = painted.width;
      out.height = painted.height;
      out.getContext('2d')!.drawImage(painted, 0, 0);
    });

    return () => cancelAnimationFrame(frame);
  }, [ready, colours, room.objects]);

  /* --- the canvas on the wall -------------------------------------------------- */

  const whole = wholeWidth(canvas);
  const measured = canvasShare(room, whole);
  // Bigger than the photographed wall: shown filling it, and said so, rather than overflowing.
  const share = measured === null ? null : Math.min(0.96, measured);

  const drag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);

    const rect = stage.current?.querySelector('.tb-room__box')?.getBoundingClientRect();

    if (!rect) return;

    // Where on the canvas it was picked up, so it does not jump to centre on the pointer.
    const start: Point = [(event.clientX - rect.left) / rect.width - hang[0], (event.clientY - rect.top) / rect.height - hang[1]];

    const move = (to: PointerEvent) => {
      setHang([
        Math.min(1, Math.max(0, (to.clientX - rect.left) / rect.width - start[0])),
        Math.min(1, Math.max(0, (to.clientY - rect.top) / rect.height - start[1])),
      ]);
    };

    const done = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', done);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', done);
  };

  // Only what the customer can actually change: no palette and no picker is not a choice.
  const changeable = useMemo(() => room.objects.filter((o) => o.palette.length || o.custom), [room.objects]);

  return (
    <>
      <div className="tb-room__stage" ref={stage}>
        <div className="tb-room__box" style={{ width: box.w, height: box.h }}>
          {/* The photograph as it is until the repainted one is ready, so nothing flashes blank. */}
          <img src={room.photo.url} alt={room.name} className="tb-room__photo" crossOrigin="anonymous" />
          {ready && <canvas ref={view} className="tb-room__photo" />}

          {share !== null && (
            <div
              className="tb-wall__art tb-room__art"
              style={{ left: `${hang[0] * 100}%`, top: `${hang[1] * 100}%`, width: `${share * 100}%` }}
              onPointerDown={drag}
              aria-label="Your canvas - drag to move it"
            >
              <CanvasPanels canvas={canvas} art={art} />
            </div>
          )}
        </div>
      </div>

      {changeable.length > 0 && (
        <div className="tb-room__colours">
          {changeable.map((object) => (
            <Swatches
              key={object.id}
              name={object.name}
              palette={object.palette}
              custom={object.custom}
              value={colours[object.id] ?? null}
              onChange={(colour) => setColours((was) => ({ ...was, [object.id]: colour }))}
            />
          ))}
        </div>
      )}

      <p className="tb-room__note">
        {measured !== null && measured > 0.96
          ? `Your canvas (${round(whole)} × ${round(canvas.h)} mm) is wider than this wall - shown as large as it fits.`
          : `Shown at its real size: ${round(whole)} × ${round(canvas.h)} mm. Drag it to move it.`}
        {problem && ` ${problem}`}
      </p>
    </>
  );
}

function round(mm: number): string {
  return String(Math.round(mm));
}

/* --------------------------------------------------------------------------
 * One object's colours
 * ----------------------------------------------------------------------- */

function Swatches({ name, palette, custom, value, onChange }: {
  name: string;
  palette: string[];
  custom: boolean;
  value: string | null;
  onChange: (colour: string | null) => void;
}) {
  // A custom colour is one that is not in the palette.
  const own = value && !palette.includes(value) ? value : null;

  return (
    <div className="tb-room__object" role="group" aria-label={name}>
      <span className="tb-room__name">{name}</span>
      <span className="tb-room__swatches">
        {/* The photograph's own colour, as a choice like the others - the way back. */}
        <button
          type="button"
          className={`tb-room__swatch tb-room__swatch--asis${value === null ? ' is-on' : ''}`}
          aria-pressed={value === null}
          aria-label={`${name}: as in the photo`}
          title="As in the photo"
          onClick={() => onChange(null)}
        />
        {palette.map((colour) => (
          <button
            key={colour}
            type="button"
            className={`tb-room__swatch${value === colour ? ' is-on' : ''}`}
            style={{ background: colour }}
            aria-pressed={value === colour}
            aria-label={`${name}: ${colour}`}
            title={colour}
            onClick={() => onChange(colour)}
          />
        ))}
        {custom && (
          <label
            className={`tb-room__swatch tb-room__swatch--pick${own ? ' is-on' : ''}`}
            style={own ? { background: own } : undefined}
            title="Any colour"
          >
            <input
              type="color"
              value={own ?? '#888888'}
              aria-label={`${name}: any colour`}
              onChange={(event) => {
                const colour = hex(event.target.value);

                if (colour) onChange(colour);
              }}
            />
            {!own && <span aria-hidden="true">+</span>}
          </label>
        )}
      </span>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * The customer's own wall
 * ----------------------------------------------------------------------- */

/**
 * A photograph of the customer's own wall, measured by them.
 *
 * The same flow as it has always been under 3D's "On a wall" - WallPhoto,
 * unchanged - reached from here too, because a customer looking at rooms is
 * exactly the one who wants to try their own.
 */
function OwnWall({ config, photo, onPhoto }: { config: ThreeConfig; photo: HTMLImageElement | null; onPhoto: (image: HTMLImageElement) => void }) {
  return (
    <div className="tb-room__stage tb-room__stage--own">
      {photo ? (
        <WallPhoto config={config} photo={photo} />
      ) : (
        <div className="tb-room__empty">
          <p>
            <strong>See it on your own wall</strong>
            <span>Take a photo of the wall straight on. It stays on this device - it is never uploaded.</span>
          </p>
          <label className="tb-btn tb-btn--primary tb-three__pick">
            <span>Choose a photo</span>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];

                if (!file) return;

                const image = new Image();

                image.onload = () => onPhoto(image);
                image.src = URL.createObjectURL(file);
                event.target.value = '';
              }}
            />
          </label>
        </div>
      )}
    </div>
  );
}
